// Purpose: the canonical Button primitive for Phase 7.5+.
//
// Variants:
//   primary  — gradient CTA from the sign-in hero. Three-stop gradient
//              accent-cta-start → accent-cta-mid → accent-cta-end at
//              135deg (blue → indigo → purple in light theme;
//              deeper-blue → indigo → deep-purple in dark), shadow-cta,
//              inset highlight.
//   secondary — transparent bg, border-subtle border, fg-primary text.
//   ghost     — no bg, no border, fg-primary text; hover darkens bg.
//   danger    — status-danger bg, white text.
//   subtle    — bg-tertiary bg, fg-primary text.
//
// Sizes: sm (32px), md (40px), lg (48px), xl (56px — sign-in hero only).
//
// States: default / hover / focus-visible (ring) / active / disabled /
// loading (spinner replaces content, width preserved).
//
// Icon slots: leftIcon / rightIcon props; if children is absent and
// only an icon is passed, button renders square (icon-only).

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { LoaderIcon } from '../icons/index.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Inline spinner replaces content; button stays the same width. */
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
}

// Variant -> class mapping. Tailwind utility classes so every color +
// shadow + radius resolves through CSS vars; theme flip works
// automatically.
const VARIANTS: Record<ButtonVariant, string> = {
  // Primary uses the gradient CTA aesthetic from the sign-in hero. The
  // gradient is defined with arbitrary-value utilities to pull the CSS
  // vars; shadow is the named `shadow-cta` token from tailwind.config.
  primary: [
    'text-white',
    'bg-[linear-gradient(135deg,var(--accent-cta-start)_0%,var(--accent-cta-mid)_50%,var(--accent-cta-end)_100%)]',
    'shadow-cta',
    'hover:shadow-cta-hover hover:-translate-y-px',
    'active:translate-y-0',
    // Focus ring is global via :focus-visible in styles.css.
  ].join(' '),

  secondary: [
    'bg-transparent border border-border-subtle text-fg-primary',
    'hover:border-border-strong hover:bg-bg-tertiary/40',
    'active:bg-bg-tertiary/60',
  ].join(' '),

  ghost: [
    'bg-transparent text-fg-primary',
    'hover:bg-bg-tertiary/50',
    'active:bg-bg-tertiary',
  ].join(' '),

  danger: ['bg-status-danger text-white', 'hover:opacity-90', 'active:opacity-80'].join(' '),

  subtle: [
    'bg-bg-tertiary text-fg-primary',
    'hover:bg-bg-secondary',
    'active:bg-surface-elevated',
  ].join(' '),
};

// Size -> height + padding + font-size tuple. The xl size is the
// sign-in hero CTA; Phase 7.5 design calls for Inter 500 (medium)
// on that variant, not 600 (semibold), to keep the white-on-gradient
// text from going into the "scratchy" regime. Other sizes keep
// semibold — they're used for in-workspace actions that benefit from
// the heavier weight.
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-s font-semibold gap-2',
  md: 'h-10 px-4 text-body-m font-semibold gap-2',
  lg: 'h-12 px-5 text-body-m font-semibold gap-3',
  xl: 'h-14 px-8 text-body-m font-medium gap-3',
};

const BASE = [
  'inline-flex items-center justify-center',
  'rounded-md',
  'font-sans',
  'whitespace-nowrap',
  'select-none',
  'cursor-pointer',
  'transition-all duration-base ease-out',
  // tabular numerals for any numbers inside button labels
  "[font-feature-settings:'tnum'_1]",
  // disabled
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0',
  // letter-spacing matches CTA 0.01em
  'tracking-[0.01em]',
].join(' ');

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    children,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  const classes = [BASE, VARIANTS[variant], SIZES[size], className ?? ''].join(' ');
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {loading ? (
        <>
          <LoaderIcon size={16} className="animate-spin" aria-hidden />
          {/* Preserve layout width by keeping content rendered but hidden. */}
          <span className="sr-only">Loading</span>
          {children ? <span className="invisible">{children}</span> : null}
        </>
      ) : (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      )}
    </button>
  );
});
